<template>
  <div class="rdm-overlay" @click.self="$emit('close')">
    <div class="rdm-modal" role="dialog" aria-modal="true" :aria-label="reportName">
      <!-- ============ Header ============ -->
      <header class="rdm-header">
        <div class="rdm-header__main">
          <h2 class="rdm-header__title">{{ reportName }}</h2>
          <div class="rdm-header__meta">
            <span v-if="report && report.created_at">{{ formatDate(report.created_at) }}</span>
            <span v-if="report && report.triggered_by && report.triggered_by.name">{{ report.triggered_by.name }}</span>
            <span v-if="siteHost">{{ siteHost }}</span>
            <span v-if="report && report.status" class="rdm-status" :class="'rdm-status--' + report.status">{{ report.status }}</span>
          </div>
        </div>
        <button type="button" class="rdm-close" aria-label="Close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <!-- ============ Summary ============ -->
      <div v-if="!loading" class="rdm-summary">
        <div class="rdm-stat">
          <span class="rdm-stat__label">Score</span>
          <span class="rdm-stat__value" :class="scoreClass">{{ summary.score !== null ? summary.score + '%' : '—' }}</span>
        </div>
        <div class="rdm-stat">
          <span class="rdm-stat__label">Errors</span>
          <span class="rdm-stat__value">{{ summary.errors }}</span>
        </div>
        <div class="rdm-stat">
          <span class="rdm-stat__label">Warnings</span>
          <span class="rdm-stat__value">{{ summary.warnings }}</span>
        </div>
        <div class="rdm-stat rdm-stat--wide">
          <span class="rdm-stat__label">{{ summary.pages }} pages</span>
          <div class="rdm-bar" :title="`${summary.good} great · ${summary.warn} need improvement · ${summary.poor} poor`">
            <span class="rdm-bar__seg rdm-bar__seg--good" :style="{ width: pct(summary.good) }"></span>
            <span class="rdm-bar__seg rdm-bar__seg--warn" :style="{ width: pct(summary.warn) }"></span>
            <span class="rdm-bar__seg rdm-bar__seg--poor" :style="{ width: pct(summary.poor) }"></span>
          </div>
          <span class="rdm-stat__legend">
            <span><i class="rdm-dot rdm-dot--ok"></i>{{ summary.good }} great</span>
            <span><i class="rdm-dot rdm-dot--warn"></i>{{ summary.warn }} need improvement</span>
            <span><i class="rdm-dot rdm-dot--error"></i>{{ summary.poor }} poor</span>
          </span>
        </div>
      </div>

      <!-- ============ Toolbar: page groups + search ============ -->
      <div class="rdm-toolbar">
        <div class="rdm-pills" role="tablist">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            role="tab"
            class="rdm-pill"
            :class="{ 'rdm-pill--active': activeTab === t.key }"
            :aria-selected="activeTab === t.key ? 'true' : 'false'"
            @click="activeTab = t.key"
          >
            <i class="rdm-dot" :class="t.dot"></i>{{ t.label }}
            <span class="rdm-pill__count">{{ groups[t.key].length }}</span>
          </button>
        </div>
        <!-- Pages with errors from one source (theme / content / files) -->
        <div class="rdm-pills" role="tablist" aria-label="Errors by source">
          <button
            v-for="t in sourceTabs"
            :key="t.key"
            type="button"
            role="tab"
            class="rdm-pill"
            :class="{ 'rdm-pill--active': activeTab === t.key }"
            :aria-selected="activeTab === t.key ? 'true' : 'false'"
            @click="activeTab = t.key"
          >
            <i class="rdm-dot" :class="t.dot"></i>{{ t.label }}
            <span class="rdm-pill__count">{{ groups[t.key].length }}</span>
          </button>
        </div>
        <div class="rdm-search">
          <i class="fas fa-search rdm-search__icon"></i>
          <input v-model.trim="search" type="text" class="rdm-search__input" placeholder="Filter pages…" aria-label="Filter pages by URL" />
          <button v-if="search" type="button" class="rdm-search__clear" aria-label="Clear search" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- ============ Body ============ -->
      <div class="rdm-body">
        <div v-if="loading" class="rdm-empty"><i class="fas fa-spinner fa-spin me-2"></i>Loading…</div>

        <div v-else-if="activeScans.length === 0" class="rdm-empty">
          <template v-if="search">No pages matching “{{ search }}” in this group.</template>
          <template v-else-if="activeTab === 'errors'"><i class="fas fa-check-circle me-2 text-success"></i>No pages with errors.</template>
          <template v-else-if="activeTab === 'warnings'"><i class="fas fa-check-circle me-2 text-success"></i>No pages with warnings only.</template>
          <template v-else-if="sourceLabels[activeTab]"><i class="fas fa-check-circle me-2 text-success"></i>No pages with {{ sourceLabels[activeTab].toLowerCase() }} errors.</template>
          <template v-else>No fully clean pages in this report.</template>
        </div>

        <div v-else class="rdm-list">
          <div class="rdm-list__head">
            <span></span>
            <span>Page</span>
            <span v-for="t in sourceTabs" :key="t.key" class="rdm-num">{{ t.label }}</span>
            <span class="rdm-num">Errors</span>
            <span class="rdm-num">Warnings</span>
          </div>

          <template v-for="s in activeScans">
            <button
              :key="s.id"
              type="button"
              class="rdm-row"
              :class="{ 'rdm-row--open': isExpanded(s.id) }"
              :aria-expanded="isExpanded(s.id) ? 'true' : 'false'"
              :title="s.url"
              @click="toggleExpanded(s.id)"
            >
              <i class="fas fa-chevron-right rdm-row__chev"></i>
              <span class="rdm-row__page">
                <span class="rdm-row__path">{{ pathOf(s) }}</span>
                <span v-if="s.entity_type" class="rdm-row__entity">{{ s.entity_type }}<template v-if="s.entity_id"> #{{ s.entity_id }}</template></span>
                <!-- span, not <a>: the row itself is a button (keyboard users get "Open page" in the expanded row) -->
                <span v-if="s.url" class="rdm-row__open" title="Open page in a new tab" aria-hidden="true" @click.stop="openPage(s)">
                  <i class="fas fa-external-link-alt"></i>
                </span>
              </span>
              <span v-for="t in sourceTabs" :key="t.key" class="rdm-num">
                <span class="rdm-count" :class="sourceCounts[s.id][t.key] ? 'rdm-count--src' : 'rdm-count--zero'">{{ sourceCounts[s.id][t.key] }}</span>
              </span>
              <span class="rdm-num"><span class="rdm-count" :class="s.errors ? 'rdm-count--error' : 'rdm-count--zero'">{{ s.errors || 0 }}</span></span>
              <span class="rdm-num"><span class="rdm-count" :class="s.warnings ? 'rdm-count--warn' : 'rdm-count--zero'">{{ s.warnings || 0 }}</span></span>
            </button>

            <!-- expanded page -->
            <div v-if="isExpanded(s.id)" :key="s.id + '-detail'" class="rdm-detail">
              <div class="rdm-detail__bar">
                <div class="rdm-pills rdm-pills--sm" role="tablist">
                  <button type="button" role="tab" class="rdm-pill" :class="{ 'rdm-pill--active': issueTabOf(s) === 'errors' }" @click.stop="setIssueTab(s.id, 'errors')">
                    Errors <span class="rdm-pill__count">{{ (s.errors_array || []).length }}</span>
                  </button>
                  <button type="button" role="tab" class="rdm-pill" :class="{ 'rdm-pill--active': issueTabOf(s) === 'warnings' }" @click.stop="setIssueTab(s.id, 'warnings')">
                    Warnings <span class="rdm-pill__count">{{ (s.warnings_array || []).length }}</span>
                  </button>
                </div>
                <div class="rdm-detail__meta">
                  <span :title="'Revision v' + s.revision">{{ formatDate(s.created_at) }}</span>
                  <a v-if="s.url" :href="s.url" target="_blank" rel="noopener" class="rdm-detail__link">Open page <i class="fas fa-external-link-alt"></i></a>
                </div>
              </div>

              <div v-if="!issuesOf(s).length" class="rdm-detail__empty">
                {{ issueTabOf(s) === 'errors' ? 'No errors on this page.' : 'No warnings on this page.' }}
              </div>

              <!-- one card per rule; its elements listed inside (acks are rule-level anyway) -->
              <ul v-else class="rdm-issues">
                <li
                  v-for="g in issueGroups(s)"
                  :key="g.key"
                  class="rdm-issue"
                  :class="['rdm-issue--' + (issueTabOf(s) === 'errors' ? 'error' : 'warn'), { 'rdm-issue--acked': g.ack }]"
                >
                  <div class="rdm-issue__head">
                    <span class="rdm-issue__title">{{ g.title }}</span>
                    <span v-if="g.files" class="rdm-issue__count">{{ g.files.length }} {{ g.files.length === 1 ? (g.rule === 'broken-link' ? 'link' : 'file') : (g.rule === 'broken-link' ? 'links' : 'files') }}</span>
                    <span v-else-if="groupCount(g) > 1" class="rdm-issue__count">× {{ groupCount(g) }}</span>
                    <span class="rdm-issue__engine">{{ g.engines }}</span>
                    <code v-if="g.rule" class="rdm-issue__rule">{{ g.rule }}</code>

                    <span v-if="g.ack" class="rdm-issue__badge" :class="'rdm-issue__badge--' + g.ack.status" title="Won’t appear in the next scan">
                      <i :class="g.ack.status === 'resolved' ? 'fas fa-check-circle' : 'fas fa-ban'" class="me-1"></i>
                      {{ g.ack.status === 'resolved' ? 'Resolved' : 'Not a warning' }}<template v-if="g.ack.is_global"> · global</template>
                    </span>
                    <span v-else-if="!g.files && g.rule && !ackForm[ackKey(s, issueTabOf(s), g.index)]" class="rdm-issue__actions">
                      <button type="button" class="rdm-ack" @click="openAck(s, issueTabOf(s), g.index, 'dismissed')"><i class="fas fa-ban me-1"></i>Not a warning</button>
                      <button type="button" class="rdm-ack rdm-ack--resolve" @click="openAck(s, issueTabOf(s), g.index, 'resolved')"><i class="fas fa-check me-1"></i>Resolved</button>
                    </span>
                  </div>

                  <!-- PDF / broken-link: one row per file, its problems inside; acks are per link -->
                  <ul v-if="g.files" class="rdm-files">
                    <li v-for="f in g.files" :key="f.index" class="rdm-file" :class="{ 'rdm-file--acked': f.item._ack }">
                      <div class="rdm-file__row">
                        <button
                          type="button"
                          class="rdm-file__main"
                          :disabled="!f.details.length"
                          :aria-expanded="f.details.length ? (isFileOpen(s, f) ? 'true' : 'false') : null"
                          @click="toggleFile(s, f)"
                        >
                          <i class="fas fa-chevron-right rdm-file__chev" :class="{ 'rdm-file__chev--open': isFileOpen(s, f), 'rdm-file__chev--none': !f.details.length }"></i>
                          <i :class="g.rule === 'broken-link' ? 'fas fa-link' : 'fas fa-file-pdf'" class="rdm-file__icon"></i>
                          <span class="rdm-file__name" :title="f.url || f.name">{{ f.name }}</span>
                        </button>
                        <span v-if="f.count" class="rdm-file__pill">{{ f.count }} {{ f.count === 1 ? 'issue' : 'issues' }}</span>
                        <a v-if="f.url" :href="f.url" target="_blank" rel="noopener" class="rdm-file__open" title="Open in a new tab" aria-label="Open in a new tab">
                          <i class="fas fa-external-link-alt"></i>
                        </a>
                        <span v-if="f.item._ack" class="rdm-issue__badge" :class="'rdm-issue__badge--' + f.item._ack.status" title="Won’t appear in the next scan">
                          {{ f.item._ack.status === 'resolved' ? 'Resolved' : 'Not a warning' }}<template v-if="f.item._ack.is_global"> · global</template>
                        </span>
                        <span v-else-if="!ackForm[ackKey(s, issueTabOf(s), f.index)]" class="rdm-issue__actions">
                          <button type="button" class="rdm-ack" @click="openAck(s, issueTabOf(s), f.index, 'dismissed')"><i class="fas fa-ban me-1"></i>Not a warning</button>
                          <button type="button" class="rdm-ack rdm-ack--resolve" @click="openAck(s, issueTabOf(s), f.index, 'resolved')"><i class="fas fa-check me-1"></i>Resolved</button>
                        </span>
                      </div>
                      <div v-if="!f.details.length && f.note" class="rdm-file__note">{{ f.note }}</div>
                      <ul v-if="isFileOpen(s, f)" class="rdm-file__details">
                        <li v-for="d in f.details" :key="d.text">
                          <span v-if="d.severity" class="rdm-sev" :class="'rdm-sev--' + d.severity.toLowerCase()">{{ d.severity }}</span>
                          <span>{{ d.text }}</span>
                          <span v-if="d.times > 1" class="rdm-issue__count">× {{ d.times }}</span>
                        </li>
                      </ul>
                      <div v-if="ackForm[ackKey(s, issueTabOf(s), f.index)]" class="rdm-ack-form">
                        <textarea
                          v-model="ackNote[ackKey(s, issueTabOf(s), f.index)]"
                          class="rdm-ack-form__input"
                          rows="2"
                          :placeholder="ackForm[ackKey(s, issueTabOf(s), f.index)] === 'resolved' ? 'What was fixed? (optional)' : 'Why is this not a warning? (optional)'"
                        ></textarea>
                        <div class="rdm-ack-form__row">
                          <span class="rdm-ack-form__hint">Applies to this link on every page of the client</span>
                          <span v-if="ackError[ackKey(s, issueTabOf(s), f.index)]" class="rdm-ack-form__error">{{ ackError[ackKey(s, issueTabOf(s), f.index)] }}</span>
                          <span class="rdm-ack-form__btns">
                            <button type="button" class="rdm-ack" :disabled="isAcking(s, issueTabOf(s), f.index)" @click="cancelAck(s, issueTabOf(s), f.index)">Cancel</button>
                            <button type="button" class="rdm-ack rdm-ack--confirm" :disabled="isAcking(s, issueTabOf(s), f.index)" @click="confirmAck(s, f.item, issueTabOf(s), f.index)">
                              {{ ackForm[ackKey(s, issueTabOf(s), f.index)] === 'resolved' ? 'Mark resolved' : 'Mark not a warning' }}
                            </button>
                          </span>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <template v-else>
                  <div v-if="g.message && g.message !== g.title" class="rdm-issue__msg">{{ g.message }}</div>
                  <ul v-if="detailsOf(g.items[0]).length" class="rdm-issue__details">
                    <li v-for="(d, di) in detailsOf(g.items[0])" :key="'d-' + di">{{ d }}</li>
                  </ul>
                  <div v-if="g.items[0].ratio !== undefined && g.items[0].foreground" class="rdm-issue__contrast">
                    ratio <strong>{{ g.items[0].ratio }}</strong>
                    · fg <span class="rdm-swatch" :style="{ background: g.items[0].foreground }"></span><code>{{ g.items[0].foreground }}</code>
                    · bg <span class="rdm-swatch" :style="{ background: g.items[0].background }"></span><code>{{ g.items[0].background }}</code>
                  </div>
                  <template v-if="g.locations.length">
                    <code
                      v-for="(loc, li) in (expandedLocs[s.id + g.key] ? g.locations : g.locations.slice(0, 3))"
                      :key="'loc' + li"
                      class="rdm-issue__loc"
                      :title="loc"
                    >{{ loc }}</code>
                    <button
                      v-if="g.locations.length > 3"
                      type="button"
                      class="rdm-issue__more"
                      @click="$set(expandedLocs, s.id + g.key, !expandedLocs[s.id + g.key])"
                    >
                      {{ expandedLocs[s.id + g.key] ? 'Show less' : `+${g.locations.length - 3} more` }}
                    </button>
                  </template>

                  <div v-if="ackForm[ackKey(s, issueTabOf(s), g.index)]" class="rdm-ack-form">
                    <textarea
                      v-model="ackNote[ackKey(s, issueTabOf(s), g.index)]"
                      class="rdm-ack-form__input"
                      rows="2"
                      :placeholder="ackForm[ackKey(s, issueTabOf(s), g.index)] === 'resolved' ? 'What was fixed? (optional)' : 'Why is this not a warning? (optional)'"
                    ></textarea>
                    <div class="rdm-ack-form__row">
                      <label class="rdm-ack-form__toggle">
                        <input type="checkbox" v-model="ackGlobal[ackKey(s, issueTabOf(s), g.index)]" />
                        Global — all clients &amp; URLs
                      </label>
                      <span v-if="ackError[ackKey(s, issueTabOf(s), g.index)]" class="rdm-ack-form__error">{{ ackError[ackKey(s, issueTabOf(s), g.index)] }}</span>
                      <span class="rdm-ack-form__btns">
                        <button type="button" class="rdm-ack" :disabled="isAcking(s, issueTabOf(s), g.index)" @click="cancelAck(s, issueTabOf(s), g.index)">Cancel</button>
                        <button type="button" class="rdm-ack rdm-ack--confirm" :disabled="isAcking(s, issueTabOf(s), g.index)" @click="confirmGroupAck(s, g, issueTabOf(s))">
                          {{ ackForm[ackKey(s, issueTabOf(s), g.index)] === 'resolved' ? 'Mark resolved' : 'Mark not a warning' }}
                        </button>
                      </span>
                    </div>
                  </div>
                  </template>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { formatUtc } from '@/utils/time';

// TWAC rules that report one finding per file / link — listed as file rows
const FILE_RULES = new Set(['pdf-accessibility', 'pdf-scan-failed', 'pdf-scan-skipped', 'broken-link']);

/**
 * Big modal that drills into one audit report and lists every audit_scan row
 * under two tabs (Pages / Files). Each row expands deploy-style to reveal the
 * raw errors_array + warnings_array for that scan, with engine, rule_id, xpath
 * / CSS selector, and contrast data when applicable.
 */
export default {
  name: 'ReportDetailsModal',
  props: {
    reportId: { type: [Number, String], required: true },
    clientId: { type: [Number, String], required: true },
  },
  data() {
    return {
      loading: true,
      report: null,
      byType: { webpage: [], file: [] },
      counts: { webpage: 0, file: 0 },
      // Top-level grouping tab: 'errors' | 'warnings' | 'passed' | 'theme' | 'content' | 'document'
      activeTab: 'errors',
      // URL filter — applies before grouping, so tab badges follow the search.
      search: '',
      expandedIds: {},
      // Per-scan tab inside expanded row: { [scanId]: 'errors' | 'warnings' }
      issueTab: {},
      // In-flight acknowledge requests, keyed by scanId-bucket-index
      acking: {},
      // Which inline ack form is open per finding: key => 'dismissed' | 'resolved' | null
      ackForm: {},
      // Note text per finding, key => string
      ackNote: {},
      // "This is global" toggle per finding, key => bool
      ackGlobal: {},
      // Inline error message per finding, key => string
      ackError: {},
      // "+N more" element list toggles, key => bool
      expandedLocs: {},
      // open PDF / link rows, key => bool
      openFiles: {},
    };
  },
  computed: {
    webpageScans() {
      return this.byType.webpage || [];
    },
    // Errors per source bucket for every page — findings carry `source`
    // (theme / content / document) from mc2's PbElementResolver.
    sourceCounts() {
      const out = {};
      for (const s of this.webpageScans) {
        const c = { theme: 0, content: 0, document: 0 };
        for (const f of s.errors_array || []) {
          if (c[f.source] !== undefined) c[f.source]++;
        }
        out[s.id] = c;
      }
      return out;
    },
    // Pages grouped the same way as the clients-table ADA badges:
    // errors = any error; warnings = warnings only; passed = fully clean.
    // The URL search narrows every group so tab badges stay meaningful.
    groups() {
      const q = this.search.toLowerCase();
      const errors = [];
      const warnings = [];
      const passed = [];
      const bySource = { theme: [], content: [], document: [] };
      for (const s of this.webpageScans) {
        if (q && !String(s.url || '').toLowerCase().includes(q)) continue;
        if ((s.errors || 0) > 0) errors.push(s);
        else if ((s.warnings || 0) > 0) warnings.push(s);
        else passed.push(s);
        for (const key in bySource) {
          if (this.sourceCounts[s.id][key]) bySource[key].push(s);
        }
      }
      return { errors, warnings, passed, ...bySource };
    },
    activeScans() {
      return this.groups[this.activeTab] || [];
    },
    tabs() {
      return [
        { key: 'errors', label: 'With errors', dot: 'rdm-dot--error' },
        { key: 'warnings', label: 'Warnings only', dot: 'rdm-dot--warn' },
        { key: 'passed', label: 'Passed', dot: 'rdm-dot--ok' },
      ];
    },
    sourceTabs() {
      return [
        { key: 'theme', label: 'Theme', dot: 'rdm-dot--theme' },
        { key: 'content', label: 'Content', dot: 'rdm-dot--content' },
        { key: 'document', label: 'Files', dot: 'rdm-dot--document' },
      ];
    },
    sourceLabels() {
      return Object.fromEntries(this.sourceTabs.map((t) => [t.key, t.label]));
    },
    // Header strip — page buckets follow the same rules as the tabs (no search)
    summary() {
      const scans = this.webpageScans;
      const poor = scans.filter((s) => (s.errors || 0) > 0).length;
      const warn = scans.filter((s) => !(s.errors || 0) && (s.warnings || 0) > 0).length;
      const r = this.report || {};
      return {
        score: r.score !== undefined && r.score !== null ? r.score : null,
        errors: r.errors !== undefined ? r.errors : scans.reduce((n, s) => n + (s.errors || 0), 0),
        warnings: r.warnings !== undefined ? r.warnings : scans.reduce((n, s) => n + (s.warnings || 0), 0),
        pages: scans.length,
        poor,
        warn,
        good: scans.length - poor - warn,
      };
    },
    scoreClass() {
      const score = this.summary.score;
      if (score === null) return '';
      if (score >= 90) return 'rdm-good';
      if (score >= 70) return 'rdm-warn';
      return 'rdm-poor';
    },
    // Every page is on the same site — show paths, keep the host in the header
    siteHost() {
      const url = (this.report && this.report.website) || (this.webpageScans[0] && this.webpageScans[0].url) || '';
      try {
        return new URL(url).host;
      } catch (e) {
        return '';
      }
    },
    reportName() {
      if (this.report && this.report.name) return this.report.name;
      return 'Report #' + this.reportId;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey);
    this.load();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/audit-reports/${this.reportId}`);
        this.report  = res?.data?.report  ?? null;
        this.byType  = res?.data?.by_type ?? { webpage: [], file: [] };
        this.counts  = res?.data?.counts  ?? { webpage: 0, file: 0 };
        // Land on the first group that has pages: errors → warnings → passed.
        if (this.groups.errors.length) this.activeTab = 'errors';
        else if (this.groups.warnings.length) this.activeTab = 'warnings';
        else this.activeTab = 'passed';
      } catch (e) {
        console.error('Report load failed', e);
      } finally {
        this.loading = false;
      }
    },

    openPage(scan) { window.open(scan.url, '_blank', 'noopener'); },
    isExpanded(id) { return !!this.expandedIds[id]; },
    toggleExpanded(id) { this.$set(this.expandedIds, id, !this.expandedIds[id]); },

    // Inner per-scan tab: default starts on whichever side has issues; "errors"
    // wins when both are present, else "warnings", else "errors".
    issueTabOf(scan) {
      if (this.issueTab[scan.id]) return this.issueTab[scan.id];
      const errCount  = (scan.errors_array  || []).length;
      const warnCount = (scan.warnings_array || []).length;
      if (errCount === 0 && warnCount > 0) return 'warnings';
      return 'errors';
    },
    setIssueTab(scanId, tab) { this.$set(this.issueTab, scanId, tab); },

    ackKey(scan, bucket, index) { return `${scan.id}-${bucket}-${index}`; },
    isAcking(scan, bucket, index) { return !!this.acking[this.ackKey(scan, bucket, index)]; },

    // Open the inline note form under a finding for the chosen action.
    openAck(scan, bucket, index, status) {
      const key = this.ackKey(scan, bucket, index);
      this.$set(this.ackForm, key, status);
      if (this.ackNote[key] === undefined) this.$set(this.ackNote, key, '');
      this.$set(this.ackGlobal, key, false);
      this.$set(this.ackError, key, null);
    },

    // Mark every finding in the loaded report with the same engine+rule as an
    // acknowledged global, so all occurrences badge immediately and none can be
    // acknowledged twice.
    markGlobalEverywhere(engine, ruleId, status, note) {
      const scans = [...(this.byType.webpage || []), ...(this.byType.file || [])];
      for (const s of scans) {
        for (const bucket of ['errors_array', 'warnings_array']) {
          const arr = s[bucket];
          if (!Array.isArray(arr)) continue;
          for (const it of arr) {
            const rid = it.rule_id || it.id;
            if ((it.engine || '') === engine && rid === ruleId && !it._ack) {
              this.$set(it, '_ack', { status, note, is_global: true });
            }
          }
        }
      }
    },

    // A link-level ack applies to the same target link on every page of the
    // client — mirror it onto every matching finding in the loaded report.
    markLinkEverywhere(engine, ruleId, target, status, note) {
      const scans = [...(this.byType.webpage || []), ...(this.byType.file || [])];
      for (const s of scans) {
        for (const bucket of ['errors_array', 'warnings_array']) {
          const arr = s[bucket];
          if (!Array.isArray(arr)) continue;
          for (const it of arr) {
            const rid = it.rule_id || it.id;
            const itTarget = it.target || (it.raw && it.raw.target) || null;
            if ((it.engine || '') === engine && rid === ruleId && itTarget === target && !it._ack) {
              this.$set(it, '_ack', { status, note, is_global: false });
            }
          }
        }
      }
    },

    // Close the inline form without saving.
    cancelAck(scan, bucket, index) {
      const key = this.ackKey(scan, bucket, index);
      this.$set(this.ackForm, key, null);
      this.$set(this.ackError, key, null);
    },

    // Save the acknowledgment: "dismissed" (not a warning) or "resolved". Writes an
    // audit_warnings row so the rule is hidden from FUTURE scans, then marks the
    // finding in place (badge appears top-right) — it stays visible so the user
    // sees what they acknowledged.
    async confirmAck(scan, item, bucket, index) {
      const ruleId = item.rule_id || item.id;
      if (!ruleId) return;

      const key = this.ackKey(scan, bucket, index);
      const status = this.ackForm[key];
      const note = (this.ackNote[key] || '').trim();
      const isGlobal = !!this.ackGlobal[key];

      this.$set(this.acking, key, true);
      this.$set(this.ackError, key, null);
      try {
        const user = this.$store.state.authUser || {};
        const by = { id: user.id ?? null, name: user.name ?? null, email: user.email ?? null };

        if (isGlobal) {
          // Global: hide this engine+rule on ALL clients/URLs — not tied to the scan.
          await api.post('/juniper/ada-auditor/warnings/global', {
            engine: item.engine,
            rule_id: ruleId,
            status,
            bucket: bucket === 'errors' ? 'error' : 'warning',
            note: note || null,
            acknowledged_by: by,
          });
        } else {
          // Link findings (TWAC broken-link / PDF) carry the target URL — sending
          // it makes the ack LINK-level: the same link stops being reported on
          // every page of this client, not just the scanned page.
          const target = item.target || (item.raw && item.raw.target) || null;
          await api.post('/juniper/ada-auditor/warnings', {
            scan_id: scan.id,
            engine: item.engine,
            rule_id: ruleId,
            target,
            status,
            note: note || null,
            acknowledged_by: by,
          });
          if (target) this.markLinkEverywhere(item.engine, ruleId, target, status, note || null);
        }

        this.$set(item, '_ack', { status, note: note || null, is_global: isGlobal });
        // A global ack applies to the same engine+rule everywhere — map it onto
        // every matching finding in the loaded report so it shows as acknowledged
        // and can't be added a second time.
        if (isGlobal) this.markGlobalEverywhere(item.engine, ruleId, status, note || null);
        this.$set(this.ackForm, key, null);
      } catch (e) {
        console.error('Acknowledge failed', e);
        this.$set(this.ackError, key, e?.response?.data?.message || 'Could not save. Try again.');
      } finally {
        this.$set(this.acking, key, false);
      }
    },

    onKey(e) {
      if (e.key === 'Escape') this.$emit('close');
    },

    pathOf(s) {
      if (s.type === 'file' && s.url) return s.url.split('/').pop() || s.url;
      try {
        const u = new URL(s.url);
        return u.host === this.siteHost ? (u.pathname + u.search) : s.url;
      } catch (e) {
        return s.url || '—';
      }
    },
    pct(n) {
      return this.summary.pages ? (100 * n) / this.summary.pages + '%' : '0%';
    },
    // Same rule on several elements → one card. Acks are rule-level per page,
    // so one acknowledgment covers the whole group.
    issueGroups(scan) {
      const groups = [];
      const byKey = {};
      this.issuesOf(scan).forEach((item, index) => {
        const key = (item.engine || '?') + '|' + (this.ruleOf(item) || this.issueTitle(item));
        if (!byKey[key]) {
          byKey[key] = {
            key,
            index,
            title: this.issueTitle(item),
            engines: this.enginesOf(item),
            rule: this.ruleOf(item),
            message: item.message || item.description || '',
            items: [],
            locations: [],
            ack: null,
          };
          groups.push(byKey[key]);
        }
        const g = byKey[key];
        g.items.push(item);
        if (FILE_RULES.has(g.rule)) {
          if (!g.files) g.files = [];
          g.files.push(this.fileRow(item, index));
          return;
        }
        if (item._ack && !g.ack) g.ack = item._ack;
        // axe keeps every failing element of a rule in raw.nodes
        const nodes = item.engine === 'AXE' && item.raw && Array.isArray(item.raw.nodes) ? item.raw.nodes : [];
        if (nodes.length) {
          nodes.forEach((n) => g.locations.push(n.xpath || (Array.isArray(n.target) ? n.target.join(' ') : String(n.target || ''))));
        } else if (this.hasLocation(item)) {
          g.locations.push(this.locationOf(item));
        }
      });
      return groups;
    },
    // One PDF / link: name from the URL (older scans only kept it in the
    // message), issue count, and its problems deduped ("[CRITICAL] X" × 2).
    fileRow(item, index) {
      const url = item.target || (item.raw && item.raw.target) || null;
      let name = '';
      if (url) {
        try {
          name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '') || url;
        } catch (e) {
          name = url;
        }
      }
      if (!name) {
        const m = /:\s*([^:]+\.pdf)\s*$/i.exec(item.message || '');
        name = m ? m[1].trim() : item.message || 'File';
      }
      const byText = {};
      const details = [];
      for (const raw of this.detailsOf(item)) {
        const m = /^\[([A-Z]+)\]\s*(.*)$/.exec(String(raw));
        const text = m ? m[2] : String(raw);
        if (byText[text]) {
          byText[text].times++;
          continue;
        }
        byText[text] = { text, severity: m ? m[1] : null, times: 1 };
        details.push(byText[text]);
      }
      let count = this.detailsOf(item).length;
      if (!count) {
        const m = /\((\d+)\s+issue/.exec(item.message || '');
        count = m ? Number(m[1]) : null;
      }
      return { index, item, url, name, count, details, note: item.message || '' };
    },
    isFileOpen(scan, file) {
      return !!this.openFiles[scan.id + '-' + file.index];
    },
    toggleFile(scan, file) {
      if (!file.details.length) return;
      const key = scan.id + '-' + file.index;
      this.$set(this.openFiles, key, !this.openFiles[key]);
    },
    // elements, not findings — one axe finding can cover many nodes
    groupCount(group) {
      return Math.max(group.items.length, group.locations.length);
    },
    async confirmGroupAck(scan, group, bucket) {
      await this.confirmAck(scan, group.items[0], bucket, group.index);
      const ack = group.items[0]._ack;
      if (ack) group.items.forEach((it) => { if (!it._ack) this.$set(it, '_ack', ack); });
    },
    issuesOf(scan) {
      return (this.issueTabOf(scan) === 'errors' ? scan.errors_array : scan.warnings_array) || [];
    },
    ruleOf(item) {
      return item.rule_id || item.id || null;
    },
    // Cross-engine title set by mc2 (AdaRuleMap), else the rule id in words
    issueTitle(item) {
      if (item.title) return item.title;
      const rule = this.ruleOf(item);
      if (!rule) return item.message || item.description || 'Issue';
      return String(rule).replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    },
    enginesOf(item) {
      return Array.isArray(item.engines) && item.engines.length ? item.engines.join(' + ') : (item.engine || '?');
    },
    hasLocation(item) {
      return !!(item.xpath || item.selector || (item.selectors && item.selectors.length) || item.target || (item.raw && item.raw.target));
    },
    rowLabel(s) {
      if (s.type === 'file' && s.url) return s.url.split('/').pop() || s.url;
      return s.url || '—';
    },

    // Render WAVE selectors[]/selector/xpath in a compact line
    locationOf(item) {
      if (item.xpath) return item.xpath;
      if (item.selector) return item.selector;
      if (item.target || (item.raw && item.raw.target)) return item.target || item.raw.target; // TWAC: link/PDF URL
      if (Array.isArray(item.selectors) && item.selectors.length) {
        return item.selectors.slice(0, 3).join('  ·  ') + (item.selectors.length > 3 ? ` (+${item.selectors.length - 3} more)` : '');
      }
      return '—';
    },

    // TWAC findings (pdf-accessibility) carry a per-item breakdown in raw.details.
    detailsOf(item) {
      const d = item.details || (item.raw && item.raw.details);
      return Array.isArray(d) ? d : [];
    },

    // Timestamps arrive in UTC (sometimes without a timezone suffix) —
    // always render the viewer's local time.
    formatDate(s) {
      if (!s) return '';
      return formatUtc(s, 'MMM D, YYYY, h:mm A');
    },
  },
};
</script>

<style scoped>
/* ====== Frame ====== */
.rdm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1080;
  padding: 4vh 2vw;
  overflow-y: auto;
}
.rdm-modal {
  width: min(1100px, 96vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 30px 60px -15px rgba(15, 23, 42, 0.35), 0 0 1px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
  font-size: 13px;
  color: #111827;
}

/* ====== Header ====== */
.rdm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 14px 20px 10px;
}
.rdm-header__main { min-width: 0; }
.rdm-header__title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rdm-header__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}
.rdm-header__meta > span + span::before {
  content: '·';
  margin: 0 8px;
  color: #d1d5db;
}
.rdm-status {
  text-transform: capitalize;
  font-weight: 500;
}
.rdm-status--finished { color: #15803d; }
.rdm-status--working { color: #1e40af; }
.rdm-close {
  flex: none;
  background: transparent;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
}
.rdm-close:hover { background: #f3f4f6; color: #111827; }

/* ====== Summary strip ====== */
.rdm-summary {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin: 0 20px 12px;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
}
.rdm-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 16px;
  border-right: 1px solid #f1f3f5;
  min-width: 90px;
}
.rdm-stat:last-child { border-right: none; }
.rdm-stat--wide { flex: 1; min-width: 260px; }
.rdm-stat__label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rdm-stat__value { font-size: 18px; font-weight: 600; line-height: 1.2; }
.rdm-good { color: #15803d; }
.rdm-warn { color: #b45309; }
.rdm-poor { color: #b91c1c; }
.rdm-bar {
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: #f3f4f6;
  margin: 4px 0 2px;
}
.rdm-bar__seg--good { background: #22c55e; }
.rdm-bar__seg--warn { background: #f59e0b; }
.rdm-bar__seg--poor { background: #ef4444; }
.rdm-stat__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #4b5563;
}

.rdm-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}
.rdm-dot--error { background: #ef4444; }
.rdm-dot--warn { background: #f59e0b; }
.rdm-dot--ok { background: #22c55e; }
.rdm-dot--theme { background: #8b5cf6; }
.rdm-dot--content { background: #3b82f6; }
.rdm-dot--document { background: #64748b; }

/* ====== Toolbar ====== */
.rdm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 20px;
  border-top: 1px solid #f1f3f5;
  border-bottom: 1px solid #f1f3f5;
}
.rdm-pills {
  display: inline-flex;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 3px;
}
.rdm-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  border-radius: 16px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
}
.rdm-pill .rdm-dot { margin-right: 0; }
.rdm-pill:hover:not(.rdm-pill--active) { color: #374151; }
.rdm-pill--active {
  background: #fff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.rdm-pill__count {
  font-size: 11px;
  color: #6b7280;
  background: #e5e7eb;
  border-radius: 9px;
  padding: 0 6px;
}
.rdm-pills--sm .rdm-pill { padding: 3px 10px; font-size: 12px; }

.rdm-search { position: relative; width: 240px; }
.rdm-search__icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 12px;
}
.rdm-search__input {
  width: 100%;
  padding: 5px 28px 5px 30px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 13px;
  outline: none;
}
.rdm-search__input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.rdm-search__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 11px;
}

/* ====== Page list ====== */
.rdm-body { overflow-y: auto; flex: 1; }
.rdm-empty { padding: 32px 20px; text-align: center; color: #6b7280; }

.rdm-list__head,
.rdm-row {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) repeat(3, 62px) 70px 70px;
  gap: 12px;
  align-items: center;
  padding: 0 20px;
}
.rdm-list__head {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fafbfc;
  height: 30px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #f1f3f5;
}
.rdm-num { text-align: right; }
.rdm-row {
  width: 100%;
  min-height: 36px;
  border: 0;
  border-bottom: 1px solid #f5f6f8;
  background: none;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.rdm-row:hover { background: #f9fafb; }
.rdm-row--open { background: #f9fafb; }
.rdm-row__chev { font-size: 10px; color: #9ca3af; transition: transform 0.15s ease; }
.rdm-row--open .rdm-row__chev { transform: rotate(90deg); }
.rdm-row__page {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.rdm-row__path {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rdm-row__entity { flex: none; font-size: 11px; color: #9ca3af; }
.rdm-row__open {
  flex: none;
  align-self: center;
  font-size: 11px;
  color: #adb5bd;
  cursor: pointer;
}
.rdm-row__open:hover { color: #2563eb; }
.rdm-count {
  display: inline-block;
  min-width: 26px;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}
.rdm-count--error { background: #fee2e2; color: #991b1b; }
.rdm-count--warn { background: #fef3c7; color: #92400e; }
.rdm-count--zero { color: #c0c4cc; font-weight: 500; }
.rdm-count--src { background: #f3f4f6; color: #374151; }

/* ====== Expanded page ====== */
.rdm-detail {
  padding: 10px 20px 14px 48px;
  background: #fbfbfc;
  border-bottom: 1px solid #f1f3f5;
}
.rdm-detail__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.rdm-detail__meta { display: flex; gap: 12px; font-size: 12px; color: #6b7280; }
.rdm-detail__link { color: #2563eb; text-decoration: none; }
.rdm-detail__link:hover { text-decoration: underline; }
.rdm-detail__link i { font-size: 10px; }
.rdm-detail__empty { color: #6b7280; padding: 6px 0; }

.rdm-issues { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.rdm-issue {
  background: #fff;
  border: 1px solid #eef0f3;
  border-left: 3px solid #ef4444;
  border-radius: 6px;
  padding: 7px 10px;
}
.rdm-issue--warn { border-left-color: #f59e0b; }
.rdm-issue--acked { opacity: 0.7; }
.rdm-issue__head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rdm-issue__title { font-weight: 600; }
.rdm-issue__count { font-size: 12px; font-weight: 600; color: #6b7280; }
.rdm-files { list-style: none; margin: 6px 0 0; padding: 0; border-top: 1px solid #f1f3f5; }
.rdm-file { border-bottom: 1px solid #f5f6f8; }
.rdm-file:last-child { border-bottom: none; }
.rdm-file--acked { opacity: 0.7; }
.rdm-file__row { display: flex; align-items: center; gap: 10px; min-height: 32px; }
.rdm-file__main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.rdm-file__main:disabled { cursor: default; }
.rdm-file__chev { font-size: 9px; color: #9ca3af; transition: transform 0.15s ease; }
.rdm-file__chev--open { transform: rotate(90deg); }
.rdm-file__chev--none { visibility: hidden; }
.rdm-file__icon { color: #dc2626; font-size: 13px; }
.rdm-file__icon.fa-link { color: #6b7280; }
.rdm-file__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rdm-file__pill {
  flex: none;
  font-size: 11px;
  font-weight: 600;
  color: #991b1b;
  background: #fee2e2;
  border-radius: 9px;
  padding: 1px 7px;
}
.rdm-file__open { flex: none; color: #9ca3af; font-size: 11px; }
.rdm-file__open:hover { color: #2563eb; }
.rdm-file .rdm-issue__actions,
.rdm-file .rdm-issue__badge { margin-left: 0; flex: none; }
.rdm-file .rdm-issue__actions { opacity: 0; }
.rdm-file__row:hover .rdm-issue__actions,
.rdm-file .rdm-issue__actions:focus-within { opacity: 1; }
.rdm-file__note { padding: 0 0 6px 37px; font-size: 12px; color: #6b7280; }
.rdm-file__details { list-style: none; margin: 0; padding: 0 0 8px 37px; display: flex; flex-direction: column; gap: 3px; font-size: 12px; color: #374151; }
.rdm-file__details li { display: flex; align-items: baseline; gap: 6px; }
.rdm-sev {
  flex: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  border-radius: 4px;
  padding: 0 5px;
  background: #f3f4f6;
  color: #4b5563;
}
.rdm-sev--critical { background: #fee2e2; color: #991b1b; }
.rdm-sev--serious,
.rdm-sev--major { background: #ffedd5; color: #9a3412; }
.rdm-sev--moderate,
.rdm-sev--minor { background: #fef3c7; color: #92400e; }
.rdm-ack-form__hint { font-size: 12px; color: #6b7280; }
.rdm-issue__more {
  border: 0;
  background: none;
  padding: 0;
  margin-top: 2px;
  font-size: 11px;
  color: #2563eb;
  cursor: pointer;
}
.rdm-issue__engine {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #4338ca;
  background: #eef2ff;
  border-radius: 4px;
  padding: 1px 5px;
}
.rdm-issue__rule { font-size: 11px; color: #9ca3af; background: none; padding: 0; }
.rdm-issue__msg { margin-top: 2px; color: #4b5563; }
.rdm-issue__details { margin: 4px 0 0; padding-left: 18px; color: #4b5563; font-size: 12px; }
.rdm-issue__loc {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
  background: none;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rdm-issue__contrast { margin-top: 4px; font-size: 12px; color: #4b5563; }
.rdm-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid #e5e7eb;
  vertical-align: middle;
  margin: 0 3px;
}
.rdm-issue__actions { margin-left: auto; display: inline-flex; gap: 6px; opacity: 0.55; transition: opacity 0.15s ease; }
.rdm-issue:hover .rdm-issue__actions,
.rdm-issue__actions:focus-within { opacity: 1; }
.rdm-issue__badge {
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  border-radius: 9px;
  padding: 1px 8px;
}
.rdm-issue__badge--resolved { background: #dcfce7; color: #166534; }
.rdm-issue__badge--dismissed { background: #f3f4f6; color: #4b5563; }

/* ====== Acknowledge ====== flat small buttons (settings convention) */
.rdm-ack {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e6e8ec;
  background: transparent;
  color: #111827;
  border-radius: 7px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
}
.rdm-ack:hover:not(:disabled) { background: #f3f5f9; }
.rdm-ack:disabled { opacity: 0.5; cursor: default; }
.rdm-ack--resolve { color: #15803d; }
.rdm-ack--confirm { color: #fff; background: #111827; border-color: #111827; }
.rdm-ack--confirm:hover:not(:disabled) { background: #1f2937; }
.rdm-ack-form { margin-top: 8px; }
.rdm-ack-form__input {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  resize: vertical;
}
.rdm-ack-form__row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 6px; }
.rdm-ack-form__toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #4b5563; margin: 0; cursor: pointer; }
.rdm-ack-form__error { color: #b91c1c; font-size: 12px; }
.rdm-ack-form__btns { margin-left: auto; display: inline-flex; gap: 6px; }
</style>
